import {
  Button,
  Modal,
  FilterSelectorOperators as Operators
} from "@etframework/ui.components";
import ReactDOM from "react-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { hideSpinner, showSpinner } from "@syncfusion/ej2-popups";

import useModalEvent from "../../hooks/useModalEvent";
import { GridUpdateEditor } from "../../components/BulkUpdateTasksModal/GridUpdateEditor";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { useBulkUpdateTasks } from "../../data/mutations/taskMutations";
import useEvent from "../../hooks/useEvent";
import { buildFilter } from "../../utils/ODataFilterBuilder";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const BulkUpdateTasksModalFromGrid = () => {
  const { t } = useTranslation(["BulkUpdateTasksModal", "Shared"], {
    useSuspense: false
  });

  const tasks = useRef([]);
  const propertyEditorRef = useRef(null);
  const updateButtonRef = useRef(null);

  const queryClient = useQueryClient();

  const { UserId } = useDueDatesAuth();
  const { currentBoardId } = useBoardProvider();
  const { mutate: bulkUpdateTasks } = useBulkUpdateTasks();
  const { Events, toggleEvent } = useEvent();

  const onSubmit = () => {
    showSpinner(modalRef.current.element);
    const body = {
      UserId: UserId,
      BoardId: currentBoardId,
      FieldsToUpdate: propertyEditorRef.current.getTaskValues(),
      TaxFieldsToUpdate: propertyEditorRef.current.getTaxReturnInfoValues()
    };

    const params = {
      $filter: buildFilter(
        "Id",
        Operators.Equal,
        tasks.current.map((x) => x.Id)
      )
    };

    bulkUpdateTasks(
      {
        data: body,
        params: params
      },
      {
        onSuccess: () => {
          closeModal();
          toast.success(t("Toasts.Success"), {
            position: "bottom-left"
          });
          toggleEvent(Events.RefreshTaskGrid);
          queryClient.invalidateQueries(["get-tags"]);
        },
        onError: () => {
          toast.error(t("Toasts.Error"), {
            position: "bottom-left"
          });
        },
        onSettled: () => {
          hideSpinner(modalRef.current.element);
        }
      }
    );
  };

  // Modal ----------------
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);

  const openModal = () => {
    setIsVisible(true);
  };

  const closeModal = () => {
    setIsVisible(false);
    tasks.current = {};
    propertyEditorRef.current?.hideTAXRelatedFields();
  };

  const toggleModal = ({ detail }) => {
    modalRef?.current?.open();
    tasks.current = detail.tasks;
  };

  useEffect(() => {
    if (tasks.current?.find?.((x) => x.TaskTypeKey === "TAX")) {
      propertyEditorRef.current.showTAXRelatedFields();
    }
  }, [isVisible]);

  useRegisterEvent(ModalEvents.BulkUpdateTaskModalFromGrid, toggleModal);

  const ModalContent = () => {
    const count = tasks.current?.length ?? 0;
    return (
      <>
        <div className="et-bulk-update-task-content d-flex flex-column gap-4 mt-3">
          <div>
            <p className="task-count-description">
              <span className="et-semantic-blue">
                {t("FromGrid.TasksSelected", { count })}
              </span>
              <span>&nbsp;{t("FromGrid.ForUpdating")}</span>
            </p>
          </div>
          <p className="prompt">{t("FromGrid.Prompt")}</p>
          <div className="overflow-auto shrink">
            <GridUpdateEditor
              onRemoveAllProperties={() =>
                updateButtonRef.current?.setDisabled(true)
              }
              onAnyChange={(e) => {
                updateButtonRef.current.setDisabled(!e.valid);
              }}
              ref={propertyEditorRef}
              id={ModalEvents.BulkUpdateTaskModalFromGrid.Id}
              currentBoardId={currentBoardId}
            />
          </div>
        </div>
      </>
    );
  };

  const ModalFooter = () => {
    return (
      <>
        <div className="d-flex gap-3 justify-content-end">
          <Button
            className={"btn-tertiary w-auto"}
            onClick={() => modalRef.current.close()}
            text={t("FromGrid.Cancel")}
          />
          <Button
            isDisabled={true}
            ref={updateButtonRef}
            onClick={onSubmit}
            className={"btn-primary w-auto"}
            text={t("FromGrid.Update")}
          />
        </div>
      </>
    );
  };

  return ReactDOM.createPortal(
    <Modal
      isModal
      id={ModalEvents.BulkUpdateTaskModalFromGrid.Id}
      ref={modalRef}
      visible={isVisible}
      header={t("FromGrid.Title")}
      content={<ModalContent />}
      useSpinner
      footerTemplate={ModalFooter}
      buttons={[{ foo: "bar" }]} //Why do i need buttons for the footer template to show up ???????
      open={openModal}
      close={closeModal}
      minHeight={"20vh"}
      height={"850px"}
      width={"800px"}
    />,
    document.body
  );
};
